import React from 'react';

import { useIntl } from 'react-intl-next';

import { MenuList } from '@atlaskit/navigation-system';
import { GlobalMoreMenu } from '@atlassian/global-more-menu';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import type { NavItemConfigInternal } from '../../common/types';
import { usePeekMenuId } from '../../controllers/peek-menu-id-providers';

import { CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID, MORE_MENU_TEST_ID } from './constants';
import { CustomizeSidebarMenuItem } from './customize-sidebar-menu-item';
import { messages } from './messages';
import MoreMenuItem from './more-menu-item';

export interface MoreMenuProps {
	appLabel: string;
	itemIds: string[];
	items: Record<string, NavItemConfigInternal>;
	onClickCustomizeSidebarMenuItem?: () => void;
	onClickMoreMenuTrigger?: () => void;
	shortcutItemIds: string[];
	shortcutItems?: Record<string, GlobalShortcutsItem>;
}

type MenuItemComponentType = React.ComponentProps<typeof GlobalMoreMenu>['menuItemComponent'];

export default function MoreMenu({
	items,
	shortcutItems = {},
	itemIds,
	shortcutItemIds,
	appLabel,
	onClickMoreMenuTrigger,
	onClickCustomizeSidebarMenuItem,
}: MoreMenuProps): React.ReactNode {
	const peekMenuId = usePeekMenuId();
	const { formatMessage } = useIntl();

	const menuItemComponent: MenuItemComponentType = React.useCallback(
		({ closeMenu, menuId }: React.ComponentProps<MenuItemComponentType>) => {
			return (
				<MoreMenuItem
					id={menuId}
					key={menuId}
					items={items}
					shortcutItems={shortcutItems}
					onClick={closeMenu}
				/>
			);
		},
		[items, shortcutItems],
	);

	const productItemsData = React.useMemo(() => {
		return itemIds.map((menuId) => ({
			menuId,
			visible: false,
		}));
	}, [itemIds]);

	const appItemsData = React.useMemo(() => {
		return shortcutItemIds.map((menuId) => ({
			menuId,
			visible: false,
		}));
	}, [shortcutItemIds]);

	return (
		<MenuList>
			<GlobalMoreMenu
				productItemsData={productItemsData}
				appItemsData={appItemsData}
				menuItemComponent={menuItemComponent}
				customizeSidebarMenuItem={
					<CustomizeSidebarMenuItem
						items={items}
						shortcuts={shortcutItems}
						testId={CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID}
						appLabel={appLabel}
						onClick={onClickCustomizeSidebarMenuItem}
					/>
				}
				moreMenuItemLabel={formatMessage(messages.moreMenuLabel)}
				peekingMenuIds={peekMenuId ? [peekMenuId] : undefined}
				testId={MORE_MENU_TEST_ID}
				onClickMoreMenuTrigger={onClickMoreMenuTrigger}
			/>
		</MenuList>
	);
}
